/*
 * Generic toolbar for client profile tables. Same left / center / right
 * layout as CasesTableToolbar: search | sort | + filter, active filter
 * chips, refresh + primary action.
 */
import type { FC, MouseEvent, PointerEvent, ReactNode } from "react";
import { ChevronDown, Plus, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { FilterChip } from "@/components/ui/filter-chip";
import { ListSearch } from "@/components/ui/list-toolbar";
import { cn } from "@/lib/utils";

import {
  KitIcon,
  KitMenu,
  ToolbarGhostButton,
  resolveKitIcon,
  type KitIconName
} from "./KitMenu";

export type ToolbarOption<V extends string = string> = {
  value: V;
  label: string;
};

export type ToolbarSearch = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  ariaLabel?: string;
};

export type ToolbarSort<V extends string = string> = {
  options: ToolbarOption<V>[];
  value: V;
  onChange: (value: V) => void;
  ariaLabel?: string;
};

export type ToolbarAddFilter = {
  options: ToolbarOption[];
  onAdd: (key: string) => void;
};

export type ToolbarPrimaryAction = {
  label: string;
  icon?: KitIconName;
  onClick: () => void;
  ariaLabel?: string;
  disabled?: boolean;
};

type Props = {
  /**
   * Renders the toolbar as the section's 36px header: the title on the left
   * and every control on the right, in one row.
   */
  title?: ReactNode;
  /** Muted sentence after the title (header mode only). */
  description?: string;
  search?: ToolbarSearch;
  sort?: ToolbarSort;
  /** "+ Filter" menu listing filters that can still be added. */
  addFilter?: ToolbarAddFilter;
  /** Active filter pills (see ToolbarFilterPill). */
  filters?: ReactNode;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  /** Either a plain primary button or a custom node (e.g. an "Add" menu). */
  primaryAction?: ToolbarPrimaryAction | ReactNode;
  /** Rendered between refresh and the primary action. */
  secondaryActions?: ReactNode;
  className?: string;
};

const isActionConfig = (
  action: ToolbarPrimaryAction | ReactNode
): action is ToolbarPrimaryAction =>
  !!action &&
  typeof action === "object" &&
  "onClick" in (action as object) &&
  "label" in (action as object);

export const ClientTableToolbar: FC<Props> = ({
  title,
  description,
  search,
  sort,
  addFilter,
  filters,
  onRefresh,
  isRefreshing = false,
  primaryAction,
  secondaryActions,
  className
}) => {
  const sortLabel = sort
    ? sort.options.find((o) => o.value === sort.value)?.label ?? "Custom"
    : null;
  const addFilterItems = addFilter
    ? addFilter.options.map((o) => ({
        key: o.value,
        label: o.label,
        onSelect: () => addFilter.onAdd(o.value)
      }))
    : [];
  const showAddFilter = addFilterItems.length > 0;
  const action = isActionConfig(primaryAction) ? primaryAction : null;
  const customAction = isActionConfig(primaryAction) ? null : primaryAction;
  const PrimaryIcon = action ? resolveKitIcon(action.icon) ?? Plus : Plus;

  const searchBox = search ? (
    <ListSearch
      className={cn("shrink", title && "w-40 min-w-20 lg:w-48")}
      aria-label={search.ariaLabel ?? "Search"}
      placeholder={search.placeholder ?? "Search..."}
      value={search.value}
      onValueChange={search.onChange}
    />
  ) : null;

  const sortMenu = sort ? (
    <KitMenu
      className="min-w-60"
      align={title ? "end" : "start"}
      trigger={
        <ToolbarGhostButton
          icon="FeatherArrowUpDown"
          ariaLabel={sort.ariaLabel ?? "Sort"}
          label={sortLabel}
        />
      }
      items={sort.options.map((o) => ({
        key: o.value,
        label: o.label,
        selected: o.value === sort.value,
        onSelect: () => sort.onChange(o.value)
      }))}
    />
  ) : null;

  const refresh = onRefresh ? (
    <ToolbarGhostButton
      icon="FeatherRefreshCw"
      ariaLabel="Refresh"
      onClick={onRefresh}
      spinning={isRefreshing}
      disabled={isRefreshing}
    />
  ) : null;

  const primary = action ? (
    <>
      <Button
        className="md:hidden"
        variant="primary"
        size="icon-sm"
        aria-label={action.ariaLabel ?? action.label}
        onClick={action.onClick}
        disabled={action.disabled}
      >
        <PrimaryIcon />
      </Button>
      <Button
        className="hidden md:inline-flex"
        variant="primary"
        onClick={action.onClick}
        disabled={action.disabled}
      >
        <PrimaryIcon />
        {action.label}
      </Button>
    </>
  ) : (
    customAction
  );

  if (title) {
    return (
      <div
        data-slot="client-section-header"
        className={cn(
          "@container flex h-10 w-full flex-none items-center gap-2 border-b border-line-1 bg-surface pr-2 pl-4",
          className
        )}
      >
        <span className="shrink-0 text-base font-medium text-fg">{title}</span>
        {description ? (
          <span
            className="hidden min-w-0 truncate text-sm text-fg-3 xl:inline"
            title={description}
          >
            {description}
          </span>
        ) : null}
        <div className="ml-auto flex min-w-0 items-center gap-2">
          {searchBox}
          {sortMenu}
          {filters}
          {refresh}
          {secondaryActions}
          {primary ? (
            <div
              className="ml-1 flex shrink-0 items-center"
              data-slot="toolbar-primary"
            >
              {primary}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div
      data-slot="client-section-header"
      className={cn(
        "flex w-full flex-none items-center gap-2 border-b border-line-2 bg-surface px-4 py-2",
        className
      )}
    >
      <div className="flex min-w-0 items-center gap-2">
        {searchBox}
        {sortMenu}

        {showAddFilter ? (
          <KitMenu
            className="min-w-40"
            trigger={
              <Button variant="ghost" size="sm" aria-label="Add filter">
                <Plus />
                <span className="hidden md:inline">Filter</span>
              </Button>
            }
            items={addFilterItems}
          />
        ) : null}
      </div>

      <div className="relative min-w-0 flex-1 overflow-hidden">
        <div className="flex scrollbar-none items-center gap-2 overflow-x-auto px-1 [&::-webkit-scrollbar]:hidden">
          {filters}
        </div>
        <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-linear-to-l from-surface to-transparent" />
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {refresh}
        {secondaryActions}
        {primary}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Filter pills                                                        */
/* ------------------------------------------------------------------ */

const RemoveFilter: FC<{ label: string; onRemove: () => void }> = ({
  label,
  onRemove
}) => (
  <span
    role="button"
    tabIndex={-1}
    aria-label={`Remove ${label.toLowerCase()} filter`}
    className="-mr-1 flex size-4 shrink-0 items-center justify-center rounded-full text-fg-3 hover:bg-fill-2 hover:text-fg"
    onPointerDown={(e: PointerEvent) => e.stopPropagation()}
    onClick={(e: MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onRemove();
    }}
  >
    <X className="size-3!" aria-hidden />
  </span>
);

type PillProps<V extends string = string> = {
  icon?: KitIconName;
  iconElement?: ReactNode;
  /** "Status" -> renders "Status: <value>". */
  label: string;
  value: V;
  /** Label for the current value; defaults to the matching option label. */
  valueLabel?: string;
  options: ToolbarOption<V>[];
  /** Adds an "All ..." item at the top that selects this value. */
  allOption?: { value: V; label: string };
  onChange: (value: V) => void;
  /** Shows the "x" and removes the pill. */
  onRemove?: () => void;
  ariaLabel?: string;
  /** `ghost`: a quiet header button ("Status: All") instead of a round chip. */
  appearance?: "chip" | "ghost";
};

export const ToolbarFilterPill = <V extends string = string>({
  icon = "FeatherFilter",
  iconElement,
  label,
  value,
  valueLabel,
  options,
  allOption,
  onChange,
  onRemove,
  ariaLabel,
  appearance = "chip"
}: PillProps<V>) => {
  const current =
    valueLabel ??
    (allOption && value === allOption.value
      ? "All"
      : options.find((o) => o.value === value)?.label ?? String(value));

  const items = [
    ...(allOption
      ? [
          {
            key: `__all__${allOption.value}`,
            label: allOption.label,
            selected: value === allOption.value,
            onSelect: () => onChange(allOption.value)
          }
        ]
      : []),
    ...options.map((o) => ({
      key: o.value,
      label: o.label,
      selected: o.value === value,
      onSelect: () => onChange(o.value)
    }))
  ];

  return (
    <KitMenu
      className="max-w-90 min-w-55"
      dividerAfter={allOption ? `__all__${allOption.value}` : undefined}
      trigger={
        appearance === "ghost" ? (
          <Button
            variant="ghost"
            className="min-w-0 shrink"
            aria-label={ariaLabel ?? `Filter by ${label.toLowerCase()}`}
          >
            {iconElement ?? <KitIcon name={icon} />}
            <span className="truncate">
              {label}: {current}
            </span>
          </Button>
        ) : (
          <FilterChip
            active
            className="max-w-full min-w-0"
            aria-label={ariaLabel ?? `Filter by ${label.toLowerCase()}`}
          >
            {iconElement ?? <KitIcon name={icon} />}
            <span className="truncate">{label}:</span>
            <span className="max-w-40 min-w-0 truncate text-fg">{current}</span>
            {onRemove ? (
              <RemoveFilter label={label} onRemove={onRemove} />
            ) : (
              <ChevronDown className="text-fg-4" aria-hidden />
            )}
          </FilterChip>
        )
      }
      items={items}
    />
  );
};

type MultiPillProps<V extends string = string> = {
  icon?: KitIconName;
  label: string;
  options: ToolbarOption<V>[];
  /** Currently checked values. */
  selected: V[];
  onToggle: (value: V) => void;
  onRemove?: () => void;
  ariaLabel?: string;
};

/** Multi-select filter pill; the menu stays open while toggling. */
export const ToolbarMultiFilterPill = <V extends string = string>({
  icon = "FeatherFilter",
  label,
  options,
  selected,
  onToggle,
  onRemove,
  ariaLabel
}: MultiPillProps<V>) => {
  const selectedSet = new Set(selected);
  const picked = options.filter((o) => selectedSet.has(o.value));
  let current: string;
  if (picked.length === 0) current = "None";
  else if (picked.length === options.length) current = "All";
  else if (picked.length <= 2) current = picked.map((o) => o.label).join(", ");
  else current = `${picked.length} selected`;

  return (
    <KitMenu
      className="max-w-90 min-w-55"
      trigger={
        <FilterChip
          active
          className="max-w-full min-w-0"
          aria-label={ariaLabel ?? `Filter by ${label.toLowerCase()}`}
        >
          <KitIcon name={icon} />
          <span className="truncate">{label}:</span>
          <span className="max-w-50 min-w-0 truncate text-fg">{current}</span>
          {onRemove ? (
            <RemoveFilter label={label} onRemove={onRemove} />
          ) : (
            <ChevronDown className="text-fg-4" aria-hidden />
          )}
        </FilterChip>
      }
      items={options.map((o) => ({
        key: o.value,
        label: o.label,
        selected: selectedSet.has(o.value),
        keepOpen: true,
        onSelect: () => onToggle(o.value)
      }))}
    />
  );
};

/** Static chip (e.g. a toggle) styled like a filter pill. */
export const ToolbarChip: FC<{
  active?: boolean;
  onClick: () => void;
  children: ReactNode;
  ariaLabel?: string;
  icon?: KitIconName;
}> = ({ active = false, onClick, children, ariaLabel, icon }) => (
  <FilterChip active={active} aria-label={ariaLabel} onClick={onClick}>
    <KitIcon name={icon} />
    {children}
  </FilterChip>
);
